import { useState } from 'react'
import { displayAction } from '../../../lib/displayApi'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

const TABS = ['My Day', 'Chores', 'Rewards', 'Calendar']

function Confetti({ active }) {
  if (!active) return null
  const colors = ['#f43f5e','#f59e0b','#10b981','#0ea5e9','#8b5cf6','#ec4899']
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 rounded-sm"
          style={{
            backgroundColor: colors[i % colors.length],
            left: `${Math.random() * 100}%`,
            top: '-10px',
            animation: `confetti-fall ${0.8 + Math.random() * 1.5}s ease-in ${Math.random() * 0.5}s forwards`,
          }}
        />
      ))}
    </div>
  )
}

export function KidPersonalView({ member, data, deviceToken, onBack }) {
  const [tab, setTab]               = useState(0)
  const [tasks, setTasks]           = useState(data.tasks ?? [])
  const [showConfetti, setConfetti] = useState(false)

  const memberColor = MEMBER_COLORS[member.memberColor] ?? '#8b5cf6'

  const myTasks  = tasks.filter((t) => t.assigned_to === member.id && !t.completed_at)
  const doneTasks = tasks.filter((t) => t.assigned_to === member.id && t.completed_at)

  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]
  const myEvents = (data.events ?? [])
    .filter((e) => e.start_time?.startsWith(todayStr) &&
      (e.event_members ?? []).some((em) => em.user_id === member.id))
    .sort((a, b) => a.start_time.localeCompare(b.start_time))

  const todayMeal = (data.mealSlots ?? []).find((s) => s.date === todayStr && s.slot_name === 'dinner')
  const dinnerName = todayMeal?.recipe?.title ?? todayMeal?.custom_name ?? 'Not planned yet'

  const hour = today.getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  async function completeTask(taskId) {
    try {
      await displayAction(deviceToken, member.id, 'complete_task', taskId)
      setTasks((prev) =>
        prev.map((t) => t.id === taskId ? { ...t, completed_at: new Date().toISOString() } : t)
      )
      setConfetti(true)
      setTimeout(() => setConfetti(false), 2000)
    } catch (_) {}
  }

  const TabMyDay = () => (
    <div className="space-y-4">
      <div className="rounded-2xl p-4 sm:p-6" style={{ backgroundColor: memberColor + '22' }}>
        <p className="text-2xl sm:text-4xl font-bold text-white mb-0.5">{greeting}, {member.displayName.split(' ')[0]}! 👋</p>
        <p className="text-gray-300 text-base sm:text-xl">{today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
      </div>

      {myEvents.length > 0 && (
        <div>
          <h3 className="text-white text-base sm:text-xl font-bold mb-2">📅 Today's Events</h3>
          {myEvents.map((e) => (
            <div key={e.id} className="bg-white/10 rounded-xl px-4 py-3 mb-2">
              <p className="text-white text-base sm:text-xl font-semibold">{e.title}</p>
              {e.location && <p className="text-gray-400 text-sm mt-0.5">{e.location}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="bg-white/10 rounded-xl px-4 py-3">
        <p className="text-gray-400 text-xs font-semibold mb-1">🍽️ What's for dinner?</p>
        <p className="text-white text-base sm:text-xl font-semibold">{dinnerName}</p>
      </div>

      {myTasks.length > 0 && (
        <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl px-4 py-3">
          <p className="text-amber-300 font-semibold text-sm sm:text-base mb-1">You have {myTasks.length} chore{myTasks.length !== 1 ? 's' : ''} to do!</p>
          <button onClick={() => setTab(1)} className="text-amber-400 text-sm underline">
            See your chores →
          </button>
        </div>
      )}
    </div>
  )

  const TabChores = () => (
    <div>
      <h3 className="text-white text-xl sm:text-2xl font-bold mb-3">My Chores</h3>
      {myTasks.length === 0 && doneTasks.length > 0 && (
        <div className="text-center py-6">
          <div className="text-5xl mb-2">🎉</div>
          <p className="text-white text-xl font-bold">You finished all your chores!</p>
        </div>
      )}
      {myTasks.length === 0 && doneTasks.length === 0 && (
        <div className="text-center py-6">
          <div className="text-4xl mb-2">🌟</div>
          <p className="text-gray-400 text-lg">No chores assigned</p>
        </div>
      )}
      <div className="space-y-2">
        {myTasks.map((task) => (
          <button
            key={task.id}
            onClick={() => completeTask(task.id)}
            className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/15 active:scale-[0.98] rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 text-left transition-all"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-4 border-gray-500 flex-shrink-0" />
            <p className="text-white text-base sm:text-xl font-semibold">{task.title}</p>
          </button>
        ))}
        {doneTasks.map((task) => (
          <div key={task.id} className="flex items-center gap-3 opacity-40 px-4 py-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-base flex-shrink-0" style={{ backgroundColor: '#10b981' }}>✓</div>
            <p className="text-gray-400 text-base line-through">{task.title}</p>
          </div>
        ))}
      </div>
    </div>
  )

  const TabCalendar = () => {
    const upcoming = (data.events ?? [])
      .filter((e) => (e.event_members ?? []).some((em) => em.user_id === member.id))
      .filter((e) => new Date(e.start_time) >= today)
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .slice(0, 10)

    let lastDate = ''
    return (
      <div>
        <h3 className="text-white text-xl sm:text-2xl font-bold mb-3">My Calendar</h3>
        {upcoming.length === 0 ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">📅</div>
            <p className="text-gray-400 text-lg">No upcoming events</p>
          </div>
        ) : upcoming.map((e) => {
          const d = e.start_time.split('T')[0]
          const showDate = d !== lastDate
          lastDate = d
          return (
            <div key={e.id}>
              {showDate && (
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mt-4 mb-1.5">
                  {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              )}
              <div className="bg-white/10 rounded-xl px-4 py-3 mb-1.5">
                <p className="text-white text-base font-semibold">{e.title}</p>
                {!e.is_all_day && (
                  <p className="text-gray-400 text-sm mt-0.5">
                    {new Date(e.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col" style={{ backgroundColor: memberColor + '18' }}>
      <Confetti active={showConfetti} />

      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white text-base sm:text-xl font-bold"
            style={{ backgroundColor: memberColor }}
          >
            {member.displaySettings?.avatar_emoji ?? member.displayName?.[0]}
          </div>
          <span className="text-white text-sm sm:text-lg font-bold">{member.displayName}</span>
        </div>
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white text-xs sm:text-sm px-3 py-1.5 rounded-xl bg-white/10 transition-colors"
        >
          ← Back
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 flex-shrink-0">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`flex-1 py-2.5 text-xs sm:text-sm font-semibold transition-colors ${
              tab === i ? 'text-white border-b-2' : 'text-gray-500 hover:text-gray-300'
            }`}
            style={tab === i ? { borderColor: memberColor } : {}}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-5 min-h-0">
        {tab === 0 && <TabMyDay />}
        {tab === 1 && <TabChores />}
        {tab === 2 && (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">⭐</div>
            <p className="text-gray-400 text-lg">Rewards coming soon</p>
          </div>
        )}
        {tab === 3 && <TabCalendar />}
      </div>
    </div>
  )
}
